import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * Safe rich-text renderer for public pages.
 *
 * - Sanitizes incoming HTML with DOMPurify (whitelist mirroring the backend
 *   bleach config: h1-h3, p, lists, blockquote, hr, br, strong, em, u, s,
 *   a, code, span/div with limited style + dir).
 * - Strips scripts, iframes, on* handlers, javascript: URIs, expressions.
 * - Wraps in `prose dark:prose-invert` Tailwind classes (typography plugin).
 *
 * If the value looks like plain text (no HTML tags), it is rendered as a
 * stack of <p> paragraphs (preserves the legacy data we haven't migrated).
 */
const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'p', 'ul', 'ol', 'li', 'blockquote',
  'hr', 'div', 'strong', 'em', 'u', 's', 'a', 'code', 'br', 'span',
];
const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'dir', 'style'];

const HAS_TAG_RE = /<\/?[a-z][\s\S]*>/i;

const RichTextRenderer = ({ value, className = '', testid = 'rich-text-renderer' }) => {
  const html = useMemo(() => {
    if (!value) return '';
    let raw = String(value);

    // Plain text fallback → wrap each blank-line block into <p>
    if (!HAS_TAG_RE.test(raw)) {
      raw = raw
        .split(/\n{2,}/)
        .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
        .join('');
    }

    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      ADD_ATTR: ['target'],
      FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    });
  }, [value]);

  if (!html) return null;

  return (
    <div
      className={[
        'prose dark:prose-invert max-w-none',
        'prose-headings:font-serif prose-headings:text-text-primary',
        'prose-p:text-text-primary prose-strong:text-text-primary',
        'prose-blockquote:text-text-secondary prose-blockquote:border-primary/40',
        'prose-a:text-primary hover:prose-a:underline',
        'prose-ul:text-text-primary prose-ol:text-text-primary',
        className,
      ].join(' ')}
      data-testid={testid}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default RichTextRenderer;
